import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { SharedModule } from '@/shared.module';
import { NnnService } from '@/services';
import type { NandaDiagnoseKenmerken, NandaDiagnoseSummary } from '@/services';
import type { NnnRegistrationRowKenmerk } from '@/api';
import type {
  AddNnnRegistrationRowDialogData,
  AddNnnRegistrationRowDialogResult,
  SaveState,
} from '../../types/careplan-nnn.types';
import { buildRowKenmerkGroepen } from '../../utils/build-row-kenmerk-groepen';
import { kenmerkKey } from '../../utils/kenmerk-key';
import { AddNnnRegistrationRowDialogComponent } from '../add-nnn-registration-row-dialog/add-nnn-registration-row-dialog.component';
import { NnnDiagnosisInfoDialogComponent } from '../nnn-diagnosis-info-dialog/nnn-diagnosis-info-dialog.component';
import { NnnNotesCellComponent } from '../nnn-notes-cell/nnn-notes-cell.component';

@Component({
  selector: 'app-nnn-diagnosis-section',
  standalone: true,
  imports: [SharedModule, NnnNotesCellComponent],
  templateUrl: './nnn-diagnosis-section.component.html',
  styleUrl: './nnn-diagnosis-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NnnDiagnosisSectionComponent {
  private readonly nnnService = inject(NnnService);
  private readonly dialog = inject(MatDialog);

  readonly patientId = input.required<string>();
  readonly diagnose = input<NandaDiagnoseSummary | null>(null);
  readonly diagnosisName = input.required<string>();
  readonly kenmerken = input.required<NnnRegistrationRowKenmerk[]>();
  readonly notes = input<string | null>(null);
  readonly editable = input(false);
  readonly showNotesCounter = input(false);
  readonly notesSaveState = input<SaveState>('');
  readonly kenmerkenSaveState = input<SaveState>('');

  readonly kenmerkenChange = output<NnnRegistrationRowKenmerk[]>();
  readonly notesInput = output<string>();
  readonly notesSave = output<void>();

  private readonly beschikbareKenmerken = signal<NandaDiagnoseKenmerken | null>(null);
  private readonly resolvedCode = signal<number | null>(null);
  private requestedCode: number | null = null;

  protected readonly labelsResolved = computed(() => {
    const code = this.diagnose()?.code ?? null;
    return code === null || this.resolvedCode() === code;
  });
  protected readonly groepen = computed(() => buildRowKenmerkGroepen(this.kenmerken(), this.beschikbareKenmerken()));
  protected readonly saveState = computed<SaveState>(() => {
    const states = [this.notesSaveState(), this.kenmerkenSaveState()];
    if (states.includes('saving')) {
      return 'saving';
    }
    return states.includes('saved') ? 'saved' : '';
  });

  constructor() {
    effect(() => {
      const code = this.diagnose()?.code ?? null;
      if (code === null || this.kenmerken().length === 0 || this.requestedCode === code) {
        return;
      }
      this.requestedCode = code;
      untracked(() => void this.loadBeschikbareKenmerken(code));
    });
  }

  private async loadBeschikbareKenmerken(code: number): Promise<void> {
    const { data } = await this.nnnService.getNandaDiagnoseKenmerken(code);
    if (this.diagnose()?.code !== code) {
      return;
    }
    if (data) {
      this.beschikbareKenmerken.set(data);
    }
    this.resolvedCode.set(code);
  }

  protected openInfo(): void {
    this.dialog.open(NnnDiagnosisInfoDialogComponent, {
      width: '720px',
      panelClass: 'card-primary',
    });
  }

  protected async openEdit(): Promise<void> {
    const diagnose = this.diagnose();
    if (!diagnose) {
      return;
    }
    const data: AddNnnRegistrationRowDialogData = {
      patientId: this.patientId(),
      diagnose,
      selectedKenmerken: this.kenmerken(),
    };
    const dialogRef = this.dialog.open(AddNnnRegistrationRowDialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      panelClass: ['card-primary', 'add-nnn-row-dialog-panel'],
      data,
    });
    const chosen: AddNnnRegistrationRowDialogResult | undefined = await firstValueFrom(dialogRef.afterClosed());
    if (!chosen) {
      return;
    }
    if (chosen.beschikbareKenmerken) {
      this.requestedCode = diagnose.code;
      this.beschikbareKenmerken.set(chosen.beschikbareKenmerken);
      this.resolvedCode.set(diagnose.code);
    }
    const existingByKey = new Map(this.kenmerken().map(k => [kenmerkKey(k.type, k.nnnKenmerkDbRef), k]));
    this.kenmerkenChange.emit(
      chosen.kenmerken.map(kenmerk => existingByKey.get(kenmerkKey(kenmerk.type, kenmerk.nnnKenmerkDbRef)) ?? kenmerk)
    );
  }
}
